/**
 * WhatsApp Test Page
 * Testar integração com WhatsApp Business API
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle, Send, Loader } from "lucide-react";

export default function WhatsAppTest() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Queries
  const validateQuery = trpc.whatsappApi.validateCredentials.useQuery({});
  const sendMessageMutation = trpc.whatsappApi.sendMessage.useMutation();
  const sendTestMutation = trpc.whatsappApi.sendTestMessage.useMutation();

  const isValid = validateQuery.data?.isValid;

  const handleSendMessage = async () => {
    if (!phoneNumber.trim() || !message.trim()) {
      setError("Preencha o número de telefone e a mensagem");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await sendMessageMutation.mutateAsync({
        phoneNumber,
        message,
      });
      setResult(response);
      setMessage("");
    } catch (err: any) {
      setError(err.message || "Erro ao enviar mensagem");
    } finally {
      setLoading(false);
    }
  };

  const handleSendTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await sendTestMutation.mutateAsync({});
      setResult(response);
    } catch (err: any) {
      setError(err.message || "Erro ao enviar mensagem de teste");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionGuard permission="whatsapp:sandbox">
    <DashboardLayout>
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">Teste do WhatsApp</h1>
        <p className="text-gray-600 mt-2">Teste a integração com WhatsApp Business API</p>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {isValid ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-500" />
                Credenciais Válidas
              </>
            ) : (
              <>
                <AlertCircle className="w-5 h-5 text-red-500" />
                Credenciais Inválidas
              </>
            )}
          </CardTitle>
          <CardDescription>
            {isValid ? "Sua integração com WhatsApp está configurada corretamente" : "Verifique suas credenciais"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status da Conexão:</span>
              <Badge variant={isValid ? "default" : "destructive"}>
                {isValid ? "Conectado" : "Desconectado"}
              </Badge>
            </div>
            {validateQuery.isLoading && (
              <div className="text-sm text-gray-600">Validando credenciais...</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Send Test Message */}
      <Card>
        <CardHeader>
          <CardTitle>Enviar Mensagem de Teste</CardTitle>
          <CardDescription>Envie uma mensagem para o número de teste configurado</CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleSendTest}
            disabled={!isValid || loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar Mensagem de Teste
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Send Custom Message */}
      <Card>
        <CardHeader>
          <CardTitle>Enviar Mensagem Personalizada</CardTitle>
          <CardDescription>Envie uma mensagem para um número específico</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Número de Telefone</label>
            <Input
              type="tel"
              placeholder="+55 11 99999-9999"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={!isValid || loading}
            />
            <p className="text-xs text-gray-500 mt-1">Formato: +55XXXXXXXXXX</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Mensagem</label>
            <Textarea
              placeholder="Digite sua mensagem aqui..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!isValid || loading}
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-1">Máximo 4096 caracteres</p>
          </div>

          <Button
            onClick={handleSendMessage}
            disabled={!isValid || loading || !phoneNumber.trim() || !message.trim()}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Enviar Mensagem
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Result */}
      {result && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="text-green-700 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Mensagem Enviada com Sucesso
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium">ID da Mensagem:</span>
                <p className="text-gray-700 break-all">{result.messageId}</p>
              </div>
              <div>
                <span className="font-medium">Resposta da API:</span>
                <pre className="bg-white p-3 rounded border mt-2 overflow-auto text-xs">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Erro ao Enviar Mensagem
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="text-blue-700">Informações</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-blue-700">
          <p>
            <strong>Número de Teste:</strong> {process.env.VITE_WHATSAPP_TEST_NUMBER || "+15558815734"}
          </p>
          <p>
            <strong>Phone Number ID:</strong> {process.env.VITE_WHATSAPP_PHONE_NUMBER_ID || "1071496606040474"}
          </p>
          <p className="text-xs mt-4">
            As credenciais são armazenadas de forma segura no servidor e não são expostas no cliente.
          </p>
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
